import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { createAppointmentInput, type AppointmentDetail } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage, HttpError } from '@/lib/api';
import { money } from '@/lib/format';
import { useBarbers, useBranches, useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { emailNote, useAvailability, useInvalidateAgenda, useServiceOptions, type CustomerOption } from './api';
import { CustomerPicker } from './customer-picker';
import { SlotPicker, type PickedSlot } from './slot-picker';
import { todayYmd, ymdOf } from './time';

export interface CreatePrefill {
  branchId?: string;
  barberId?: string;
  serviceId?: string;
  date?: string;
  /** início clicado na grade (ISO) */
  startsAt?: string;
  customer?: CustomerOption;
  fromWaitlistId?: string;
}

const ANY = '__any';
type FormValues = { branchId: string; barberId: string; serviceId: string; customerId: string; startsAt: string; notes?: string | null; status: 'requested' | 'confirmed'; fromWaitlistId?: string };

export function AppointmentFormModal({ open, onOpenChange, prefill, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; prefill: CreatePrefill; onCreated: (a: AppointmentDetail) => void }) {
  const tz = useTimezone();
  const { barberId: ownBarberId, can } = useSession();
  const branches = useBranches();
  const barbers = useBarbers();
  const services = useServiceOptions();
  const invalidate = useInvalidateAgenda();
  const restricted = !can('agenda.read_all');

  const form = useForm<FormValues>({
    resolver: zodResolver(createAppointmentInput) as never,
    defaultValues: { status: 'confirmed', notes: '' },
  });
  const [anyBarber, setAnyBarber] = useState(false);
  const [date, setDate] = useState('');
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [picked, setPicked] = useState<PickedSlot | null>(null);

  // reinicia ao abrir
  useEffect(() => {
    if (!open) return;
    const branchId = prefill.branchId ?? branches.data?.find((b) => b.active)?.id ?? '';
    form.reset({
      branchId,
      barberId: restricted ? (ownBarberId ?? '') : (prefill.barberId ?? ''),
      serviceId: prefill.serviceId ?? '',
      customerId: prefill.customer?.id ?? '',
      startsAt: prefill.startsAt ?? '',
      notes: '',
      status: 'confirmed',
      fromWaitlistId: prefill.fromWaitlistId,
    });
    setAnyBarber(!prefill.barberId && !restricted);
    setDate(prefill.date ?? (prefill.startsAt ? ymdOf(prefill.startsAt, tz) : todayYmd(tz)));
    setCustomer(prefill.customer ?? null);
    setPicked(prefill.startsAt && prefill.barberId ? { startsAt: prefill.startsAt, barberId: prefill.barberId } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const branchId = form.watch('branchId');
  const serviceId = form.watch('serviceId');
  const barberId = form.watch('barberId');

  const service = services.data?.find((s) => s.id === serviceId);
  const barberOptions = useMemo(
    () => (barbers.data ?? []).filter((b) => b.active && b.branchId === branchId && (!serviceId || b.serviceIds.includes(serviceId)) && (!restricted || b.id === ownBarberId)),
    [barbers.data, branchId, serviceId, restricted, ownBarberId],
  );
  const barberNames = useMemo(() => new Map((barbers.data ?? []).map((b) => [b.id, b.displayName])), [barbers.data]);

  const avail = useAvailability({ serviceId: serviceId || undefined, branchId: branchId || undefined, barberId: anyBarber ? undefined : barberId || undefined, date: date || undefined });

  const pickSlot = (s: PickedSlot) => {
    setPicked(s);
    form.setValue('startsAt', s.startsAt, { shouldValidate: true });
    form.setValue('barberId', s.barberId, { shouldValidate: true });
  };

  const save = useMutation({
    mutationFn: (v: FormValues) => api.post<AppointmentDetail>('/appointments', v),
    onSuccess: (a) => {
      toast.success('Agendamento criado');
      const note = emailNote(a.emailStatus);
      if (note) toast.message(note);
      invalidate();
      onCreated(a);
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof HttpError && e.status === 409) {
        toast.error('Esse horário acabou de ser ocupado. Escolha outro.');
        avail.refetch();
        setPicked(null);
        form.setValue('startsAt', '');
      } else if (e instanceof HttpError && e.fieldErrors() && Object.keys(e.fieldErrors()).length) {
        for (const [k, m] of Object.entries(e.fieldErrors())) form.setError(k as keyof FormValues, { message: m });
      } else toast.error(errorMessage(e));
    },
  });

  const errors = form.formState.errors;
  const onSubmit = form.handleSubmit((v) => save.mutate(v));

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo agendamento"
      description="Os horários listados já consideram expediente, bloqueios, feriados e outros agendamentos."
      size="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="appointment-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar agendamento'}
          </Button>
        </>
      }
    >
      <form id="appointment-form" onSubmit={onSubmit} className="grid gap-5" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="ap-branch" label="Unidade" error={errors.branchId?.message}>
            <Controller
              control={form.control}
              name="branchId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={(v) => (field.onChange(v), setPicked(null), form.setValue('startsAt', ''), form.setValue('barberId', ''))}>
                  <SelectTrigger id="ap-branch" className="w-full">
                    <SelectValue placeholder="Escolha a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {(branches.data ?? []).filter((b) => b.active).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="ap-service" label="Serviço" error={errors.serviceId?.message}>
            <Controller
              control={form.control}
              name="serviceId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={(v) => (field.onChange(v), setPicked(null), form.setValue('startsAt', ''))}>
                  <SelectTrigger id="ap-service" className="w-full">
                    <SelectValue placeholder="Escolha o serviço" />
                  </SelectTrigger>
                  <SelectContent>
                    {(services.data ?? []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {s.durationMinutes} min · {money(s.priceCents)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="ap-barber" label="Profissional" error={errors.barberId?.message}>
            <Select
              value={anyBarber ? ANY : barberId}
              onValueChange={(v) => {
                setPicked(null);
                form.setValue('startsAt', '');
                if (v === ANY) {
                  setAnyBarber(true);
                  form.setValue('barberId', '');
                } else {
                  setAnyBarber(false);
                  form.setValue('barberId', v, { shouldValidate: true });
                }
              }}
            >
              <SelectTrigger id="ap-barber" className="w-full">
                <SelectValue placeholder="Escolha o profissional" />
              </SelectTrigger>
              <SelectContent>
                {!restricted && <SelectItem value={ANY}>Qualquer disponível</SelectItem>}
                {barberOptions.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="ap-date" label="Data">
            <Input
              id="ap-date"
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setPicked(null);
                form.setValue('startsAt', '');
              }}
            />
          </Field>
        </div>

        <div className="grid gap-2">
          <p className="text-sm font-medium" id="ap-slots-label">
            Horário {service && <span className="font-normal text-muted-foreground">({service.durationMinutes} min)</span>}
          </p>
          {!serviceId ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">Escolha o serviço para ver os horários livres.</p>
          ) : (
            <SlotPicker
              slots={avail.data}
              loading={avail.isLoading}
              error={avail.error}
              onRetry={() => avail.refetch()}
              selected={picked}
              onSelect={pickSlot}
              tz={tz}
              barberNames={barberNames}
              showBarber={anyBarber}
              extra={prefill.startsAt && prefill.barberId && (anyBarber || prefill.barberId === barberId) && ymdOf(prefill.startsAt, tz) === date ? { startsAt: prefill.startsAt, barberId: prefill.barberId } : null}
            />
          )}
          {(errors.startsAt?.message || (!picked && form.formState.isSubmitted)) && (
            <p role="alert" className="text-xs text-destructive">
              Escolha um horário disponível.
            </p>
          )}
        </div>

        <Field id="customer-search" label="Cliente" error={errors.customerId?.message ? 'Escolha ou cadastre o cliente.' : undefined}>
          <CustomerPicker
            value={customer}
            onChange={(c) => {
              setCustomer(c);
              form.setValue('customerId', c?.id ?? '', { shouldValidate: form.formState.isSubmitted });
            }}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <Field id="ap-notes" label="Observações">
            <Textarea id="ap-notes" rows={2} {...form.register('notes')} />
          </Field>
          <Field id="ap-status" label="Situação inicial">
            <Controller
              control={form.control}
              name="status"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="ap-status" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmado</SelectItem>
                    <SelectItem value="requested">Solicitado</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>
        {customer && !customer.email && <p className="text-xs text-muted-foreground">Este cliente não tem e-mail: não receberá confirmação nem lembrete.</p>}
      </form>
    </FormModal>
  );
}

